import { changeBrowserLanguage, defaultLanguage, getCurrentLanguage, type SupportedLanguage } from '@modernloop/i18n';
import { useEffect } from 'react';

import { useSelectableLocales } from './useLocales';

/**
 * Enforces that the active i18n locale is one allowed by the `languages` feature flag.
 *
 * When the app initializes, i18n picks the language from localStorage or the browser
 * before the feature flag is available. This hook runs inside the React tree (after
 * LaunchDarkly loads) and switches back to the default language if the current
 * language is not in the allowed list.
 *
 * Uses useSelectableLocales() (the FF universe) rather than useLocales() (the org-enabled list)
 * because enforcement must be gated by the global FF allowlist, not the org's current selection.
 *
 * Place this hook inside the LaunchDarkly provider in each app entry point.
 */
export function useEnforceAllowedLocale(): void {
  const allowedLocales = useSelectableLocales();

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect bridges form state with changing inputs from outside this component.
  useEffect(() => {
    // SAFETY: i18n is initialized with supported locales before this hook runs.
    const currentLanguage = getCurrentLanguage() as SupportedLanguage;

    if (!allowedLocales.includes(currentLanguage)) {
      void changeBrowserLanguage(defaultLanguage);
    }
  }, [allowedLocales]);
}
